import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Checkbox, Divider, Group, Space, Stack, Tabs } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import footercenter from '#assets/images/footer-center.png'
import footerleft from '#assets/images/footer-left.png'
import footerright from '#assets/images/footer-right.png'
import headercenter from '#assets/images/header-center.png'
import headerleft from '#assets/images/header-left.png'
import headerright from '#assets/images/header-right.png'
import {
  parseDocFooters,
  parseDocHeaders,
  setFootersConfig,
  setHeadersConfig,
} from '#choo-app/lib/editor/plugins/configData'
import { ModalShell, useModalControls } from '#components/Modals'
import { useMst } from '#state'
import {
  ScriptPrintPreferences,
  SlotConfig,
  SlotPosition,
} from '#util/zodSchemas/printPreferences'

import { Example } from '../PrintScriptModal/Example'
import { isInactiveConfig } from '../PrintScriptModal/helpers'
import { SlotSelect } from '../PrintScriptModal/SlotSelect'
import { usePrintScripts } from '../PrintScriptModal/usePrintScripts'

import styles from './HeadersFootersModal.module.scss'

type Props = {
  enableDuration: boolean
}

const ScrollingContent = ({ children }: { children: React.ReactNode }) => (
  <Stack className={styles.hfModal_scrollingContent}>{children}</Stack>
)

const PrefDivider = () => <Divider mt={2} mb={2} />

const PrefStack = ({ children }: { children: React.ReactNode }) => (
  <Stack gap={5} px={10} align="center">
    {children}
  </Stack>
)

const HeadersFooters = observer(function PrintPrefsParams({
  enableDuration,
}: Props) {
  const { currentScript } = useMst()
  const { loading, errorMessage } = usePrintScripts()
  const docAttrs = currentScript?.pmEditor.editorState?.doc.attrs
  const prefs = {
    headers: parseDocHeaders(docAttrs?.headers),
    footers: parseDocFooters(docAttrs?.footers),
  }
  const controls = useModalControls()

  const updateHeaders = (value: Partial<ScriptPrintPreferences['headers']>) => {
    const command = setHeadersConfig({ ...prefs.headers, ...value })
    currentScript?.pmEditor.invoke(command)
  }

  const updateFooters = (value: Partial<ScriptPrintPreferences['footers']>) => {
    const command = setFootersConfig({ ...prefs.footers, ...value })
    currentScript?.pmEditor.invoke(command)
  }

  const updateHeaderOrFooterSlot = ({
    type,
    position,
    config,
  }: {
    type: 'header' | 'footer'
    position: SlotPosition
    config?: SlotConfig
  }) => {
    const currentSlots =
      type === 'header' ? prefs.headers.slots : prefs.footers.slots

    const newValue = {
      ...currentSlots,
      [position]: config,
    }
    if (type === 'header') {
      updateHeaders({ slots: newValue })
    } else {
      updateFooters({ slots: newValue })
    }
  }

  const updateHeaderSlot = (position: SlotPosition, config?: SlotConfig) =>
    updateHeaderOrFooterSlot({
      type: 'header',
      position,
      config,
    })

  const updateFooterSlot = (position: SlotPosition, config?: SlotConfig) =>
    updateHeaderOrFooterSlot({
      type: 'footer',
      position,
      config,
    })

  return (
    <ModalShell
      title="Headers and footers"
      size={750}
      opened={controls.opened}
      onClose={controls.onClose}
      centered={false}
      loading={loading}
      errorMessage={errorMessage}
    >
      <Tabs classNames={{ tab: styles.hfModal_tab }} defaultValue="header">
        <Tabs.List>
          <Tabs.Tab value="header">Header</Tabs.Tab>
          <Tabs.Tab value="footer">Footer</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="header" pt="xs">
          <ScrollingContent>
            <Stack gap={15}>
              <Stack gap={10} px={15} className={styles.hfModal_checkStack}>
                <Space h={2} />
                <Checkbox
                  label="Include header"
                  checked={prefs.headers.enabled}
                  onChange={(evt) => {
                    updateHeaders({
                      enabled: evt.target.checked,
                    })
                  }}
                />
                <Checkbox
                  px={30}
                  label="Show header on first page"
                  disabled={!prefs.headers.enabled}
                  checked={prefs.headers.showOnFirstPage}
                  onChange={(evt) =>
                    updateHeaders({
                      showOnFirstPage: evt.target.checked,
                    })
                  }
                />
                <Checkbox
                  px={30}
                  label="Horizontal rule appears below text"
                  disabled={!prefs.headers.enabled}
                  checked={prefs.headers.divider}
                  onChange={(evt) =>
                    updateHeaders({
                      divider: evt.target.checked,
                    })
                  }
                />
              </Stack>
              <PrefDivider />
              <Group
                align="center"
                justify="space-between"
                className={styles.hfModal_group___selects}
                gap={10}
                px={10}
              >
                <PrefStack>
                  <img src={headerleft} />
                  <SlotSelect
                    disabled={!prefs.headers.enabled}
                    label="Left"
                    config={prefs.headers.slots.left}
                    setContent={(value) => {
                      updateHeaderSlot('left', value)
                    }}
                    inactive={isInactiveConfig(
                      prefs.headers.slots.left,
                      !enableDuration,
                    )}
                  />
                </PrefStack>
                <PrefStack>
                  <img src={headercenter} />
                  <SlotSelect
                    disabled={!prefs.headers.enabled}
                    label="Center"
                    config={prefs.headers.slots.center}
                    setContent={(value) => updateHeaderSlot('center', value)}
                    inactive={isInactiveConfig(
                      prefs.headers.slots.center,
                      !enableDuration,
                    )}
                  />
                </PrefStack>
                <PrefStack>
                  <img src={headerright} />
                  <SlotSelect
                    disabled={!prefs.headers.enabled}
                    label="Right"
                    config={prefs.headers.slots.right}
                    setContent={(value) => updateHeaderSlot('right', value)}
                    inactive={isInactiveConfig(
                      prefs.headers.slots.right,
                      !enableDuration,
                    )}
                  />
                </PrefStack>
              </Group>
              <PrefDivider />
              <Example
                slots={prefs.headers.slots}
                hr={prefs.headers.divider}
                hidden={!prefs.headers.enabled}
                isScreenplay={!enableDuration}
              />
            </Stack>
          </ScrollingContent>
        </Tabs.Panel>
        <Tabs.Panel value="footer" pt="xs">
          <ScrollingContent>
            <Stack gap={15}>
              <Stack gap={10} px={15} className={styles.hfModal_checkStack}>
                <Space h={2} />
                <Checkbox
                  label="Include footer"
                  checked={prefs.footers.enabled}
                  onChange={(evt) => {
                    updateFooters({
                      enabled: evt.target.checked,
                    })
                  }}
                />
                <Checkbox
                  px={30}
                  label="Show footer on first page"
                  disabled={!prefs.footers.enabled}
                  checked={prefs.footers.showOnFirstPage}
                  onChange={(evt) => {
                    updateFooters({
                      showOnFirstPage: evt.target.checked,
                    })
                  }}
                />
                <Checkbox
                  px={30}
                  disabled={!prefs.footers.enabled}
                  label="Horizontal rule appears above text"
                  checked={prefs.footers.divider}
                  onChange={(evt) => {
                    updateFooters({
                      divider: evt.target.checked,
                    })
                  }}
                />
              </Stack>
              <PrefDivider />
              <Group
                className={styles.hfModal_group___selects}
                align="center"
                justify="space-between"
                gap={10}
                px={10}
              >
                <PrefStack>
                  <img src={footerleft} />
                  <SlotSelect
                    disabled={!prefs.footers.enabled}
                    label="Left"
                    config={prefs.footers.slots.left}
                    setContent={(value) => updateFooterSlot('left', value)}
                    inactive={isInactiveConfig(
                      prefs.footers.slots.left,
                      !enableDuration,
                    )}
                    isFooter
                  />
                </PrefStack>
                <PrefStack>
                  <img src={footercenter} />
                  <SlotSelect
                    disabled={!prefs.footers.enabled}
                    label="Center"
                    config={prefs.footers.slots.center}
                    setContent={(value) => updateFooterSlot('center', value)}
                    inactive={isInactiveConfig(
                      prefs.footers.slots.center,
                      !enableDuration,
                    )}
                    isFooter
                  />
                </PrefStack>
                <Stack gap={5} px={10} align="center">
                  <img src={footerright} />
                  <SlotSelect
                    disabled={!prefs.footers.enabled}
                    label="Right"
                    config={prefs.footers.slots.right}
                    setContent={(value) => updateFooterSlot('right', value)}
                    inactive={isInactiveConfig(
                      prefs.footers.slots.right,
                      !enableDuration,
                    )}
                    isFooter
                  />
                </Stack>
              </Group>
              <PrefDivider />
              <Example
                slots={prefs.footers.slots}
                hr={prefs.footers.divider}
                mode="footer"
                hidden={!prefs.footers.enabled}
                isScreenplay={!enableDuration}
              />
            </Stack>
          </ScrollingContent>
          <Space h={2} />
        </Tabs.Panel>
      </Tabs>
    </ModalShell>
  )
})

export const HeadersFootersModal = NiceModal.create((props: Props) => (
  <HeadersFooters {...props} />
))
